import { cn } from "@/lib/utils"
import type { SelectVoiceItemType } from "@/shared/types"
import { useEffect, useRef, useState } from "react"
import { Spinner } from "@/components/ui/spinner"
import { AudioWaveform } from "@/components/ui/audio-waveform"
import { Play, Square } from "lucide-react"
import styles from "./styles.module.scss"
import { useTheme } from "@/components/theme-provider"
import { TypographyP } from "@/components/ui/typography"
import { useUtilityStore } from "@/entities"

const VOICES_EXTENSION = "mp3"

export const SelectVoiceItem = ({
  gradient,
  id,
  isActive,
  name,
  description,
  onSelectVoice,
}: SelectVoiceItemType & { isActive: boolean; onSelectVoice: () => void }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [progress, setProgress] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const stopAllVoiceItems = useUtilityStore(s => s.stopAllVoiceItems)
  const stopAllVoiceItemsCounter = useUtilityStore(
    s => s.stopAllVoiceItemsCounter
  )
  const { theme } = useTheme()

  const updateProgress = () => {
    if (audioRef.current && audioRef.current.duration) {
      const newProgress =
        (audioRef.current.currentTime / audioRef.current.duration) * 100
      setProgress(newProgress)
      animationFrameRef.current = requestAnimationFrame(updateProgress)
    }
  }

  const handleStop = () => {
    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    if (audioRef.current) {
      // Remove all event listeners to prevent any callbacks
      audioRef.current.ontimeupdate = null
      audioRef.current.onended = null
      audioRef.current.onerror = null
      audioRef.current.oncanplaythrough = null

      // Stop the audio
      audioRef.current.pause()
      audioRef.current.currentTime = 0

      // Clear the reference
      audioRef.current = null
    }
    setIsPlaying(false)
    setIsDownloading(false)
    setProgress(0)
  }

  const handlePlay = async () => {
    const audioUrl = `${import.meta.env.VITE_BLOB_STORAGE_URL}/voices/${id}.${VOICES_EXTENSION}`
    stopAllVoiceItems()
    onSelectVoice()
    setTimeout(async () => {
      try {
        // Check if audio exists in cache using fetch with cache: 'only-if-cached'
        const response = await fetch(audioUrl, {
          method: "HEAD",
          cache: "only-if-cached",
        })

        if (response.ok) {
          // Audio exists in cache, play directly
          const audio = new Audio(audioUrl)
          audioRef.current = audio
          audio.play()
          setIsPlaying(true)
          setProgress(0)

          // Start smooth progress updates
          updateProgress()

          audio.onended = () => {
            if (animationFrameRef.current) {
              cancelAnimationFrame(animationFrameRef.current)
              animationFrameRef.current = null
            }
            setIsPlaying(false)
            setProgress(0)
            audioRef.current = null
          }
          audio.onerror = () => {
            if (animationFrameRef.current) {
              cancelAnimationFrame(animationFrameRef.current)
              animationFrameRef.current = null
            }
            setIsPlaying(false)
            setProgress(0)
            audioRef.current = null
            // If cached version fails, try downloading
            downloadAndPlay()
          }
        } else {
          // Audio not in cache, need to download
          downloadAndPlay()
        }
      } catch {
        // Cache check failed, need to download
        downloadAndPlay()
      }
    }, 100)
  }

  const downloadAndPlay = async () => {
    const audioUrl = `${import.meta.env.VITE_BLOB_STORAGE_URL}/voices/${id}.${VOICES_EXTENSION}`

    setIsDownloading(true)

    try {
      const audio = new Audio(audioUrl)
      audioRef.current = audio
      audio.preload = "auto"

      audio.oncanplaythrough = () => {
        setIsDownloading(false)
        audio.play()
        setIsPlaying(true)
        setProgress(0)
        // Start smooth progress updates
        updateProgress()
      }

      audio.onended = () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = null
        }
        setIsPlaying(false)
        setProgress(0)
        audioRef.current = null
      }
      audio.onerror = () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = null
        }
        setIsDownloading(false)
        setIsPlaying(false)
        setProgress(0)
        audioRef.current = null
        console.error("Failed to load audio:", audioUrl)
      }

      // Trigger download
      audio.load()
    } catch (error) {
      setIsDownloading(false)
      audioRef.current = null
      console.error("Error downloading audio:", error)
    }
  }

  useEffect(() => {
    return () => {
      handleStop()
    }
  }, [])

  useEffect(() => {
    handleStop()
  }, [stopAllVoiceItemsCounter])

  return (
    <div className={styles.wrapper}>
      <div
        className={cn(styles.item, {
          [styles.item_active]: isActive,
        })}
        onClick={handlePlay}
      >
        <div
          className={styles.gradientCircle}
          style={{
            background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]}, ${gradient[2]})`,
          }}
        />
        <span className={styles.item__label}>{name}</span>
        {description ? (
          <TypographyP className={styles.item__description}>
            {description}
          </TypographyP>
        ) : (
          <div className={styles.item__spacer} />
        )}
      </div>

      <button
        type="button"
        className={styles.playerWrapper}
        onClick={e => {
          e.stopPropagation()
          if (isPlaying) {
            handleStop()
          } else {
            handlePlay()
          }
        }}
        disabled={isDownloading}
      >
        {isPlaying && (
          <div
            className={cn(styles.progressBar, {
              [styles.progressBar_forWhiteTheme]: theme === "light",
            })}
            style={{ width: `${progress}%` }}
          />
        )}
        <div className={styles.buttonContent}>
          {isDownloading ? (
            <Spinner className="size-6" />
          ) : isPlaying ? (
            <>
              <AudioWaveform isPlaying={isPlaying} />
              <Square className="ml-2 size-4" />
            </>
          ) : (
            <Play />
          )}
        </div>
      </button>
    </div>
  )
}
